import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { ADS_ENABLED } from "@/const";
import { adsExcludedPath } from "@/lib/adPlacement";
import { allowed, readConsent, visitorIsRegulated } from "@/lib/consent";
import { AD_AFTER_TOOL_RESERVE_PX, AD_LOWER_RESERVE_PX } from "@/lib/layoutReserve";

interface AdBannerProps {
  slot: string;
  format?: "auto" | "horizontal" | "vertical" | "rectangle" | "fluid";
  layout?: "in-article";
  className?: string;
  /** Minimum height (px) kept free while the ad loads, so the content below does not jump when it fills.
   * Only for units that sit BELOW the tool; never reserve space above a tool's inputs. */
  reserve?: number;
}

/** How long to wait for Google's own filled/unfilled answer before treating the slot as empty (ad blocker, script blocked). */
const FILL_TIMEOUT_MS = 8000;

/**
 * Google AdSense banner component.
 *
 * IMPORTANT: Ethical ad filtering is configured at the AdSense account level:
 * - Block categories: Adult, Gambling, Alcohol, Tobacco, Pharmaceuticals, Dating
 * - Block sensitive categories in AdSense Settings > Blocking Controls
 * - Enable "Family safe" content filter
 *
 * This component renders the ad unit; filtering is handled by Google's policy settings.
 *
 * An unfilled slot (no ad for this visitor, or the script is blocked) collapses completely: no empty box, no
 * "Advertisement" label and no reserved gap are left behind.
 */
export function AdBanner({
  slot,
  format = "auto",
  layout,
  className = "",
  reserve,
}: AdBannerProps) {
  const adRef = useRef<HTMLDivElement>(null);
  const insRef = useRef<HTMLModElement>(null);
  const loaded = useRef(false);
  const [status, setStatus] = useState<"pending" | "filled" | "empty">("pending");

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;

    try {
      // @ts-ignore
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {
      // AdSense not loaded (ad blocker or dev environment)
    }
  }, []);

  // Google writes data-ad-status="filled" | "unfilled" on the <ins> once it has decided.
  useEffect(() => {
    const ins = insRef.current;
    if (!ins) return;
    const read = () => {
      const v = ins.getAttribute("data-ad-status");
      if (v === "filled") setStatus("filled");
      else if (v === "unfilled") setStatus("empty");
    };
    read();
    const mo = new MutationObserver(read);
    mo.observe(ins, { attributes: true, attributeFilter: ["data-ad-status"] });
    const t = window.setTimeout(() => setStatus((s) => (s === "pending" ? "empty" : s)), FILL_TIMEOUT_MS);
    return () => { mo.disconnect(); window.clearTimeout(t); };
  }, []);

  // The container stays in the DOM when empty (Google already holds a reference to the <ins>) but takes no space.
  const collapsed = status === "empty";
  const minHeight = reserve && status === "pending" ? `${reserve}px` : undefined;

  return (
    <div
      className={`ad-container ${className}`}
      data-ad-slot-wrapper={slot}
      data-ad-status-wrapper={status}
      style={{ minHeight, display: collapsed ? "none" : undefined }}
      ref={adRef}
    >
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: "block", maxWidth: "100%", textAlign: layout ? "center" : undefined }}
        data-ad-client="ca-pub-4226631265041590"
        data-ad-slot={slot}
        data-ad-format={format}
        data-ad-layout={layout}
        data-full-width-responsive={layout ? undefined : "true"}
      />
      {status === "filled" && (
        <p className="text-[10px] text-muted-foreground/50 text-center mt-1">
          Advertisement
        </p>
      )}
    </div>
  );
}

/**
 * Whether a manual ad unit may render right now: ads switched on, not a Pro subscriber (once the plan is known),
 * not a payment/sign-in/account/API-key page, and (in the EEA, UK and Switzerland) the visitor opted in to
 * advertising. Without that last check the unit used to render as an empty reserved box, because the ad script
 * is never loaded for those visitors.
 */
export function useAdsAllowed(): boolean {
  const { isAuthenticated } = useAuth();
  const [location] = useLocation();
  const billingStatus = trpc.billing.status.useQuery(undefined, {
    enabled: isAuthenticated,
  });
  const [consentVersion, setConsentVersion] = useState(0);
  useEffect(() => {
    const h = () => setConsentVersion((v) => v + 1);
    window.addEventListener("fdp-consent-saved", h);
    return () => window.removeEventListener("fdp-consent-saved", h);
  }, []);

  if (!ADS_ENABLED) return false;
  if (adsExcludedPath(location)) return false;
  // While we don't yet know Pro status, don't flash an ad that might
  // immediately disappear -- wait for a definite answer.
  if (isAuthenticated && billingStatus.isLoading) return false;
  if (billingStatus.data?.isPro) return false;
  void consentVersion; // re-evaluate after the visitor saves a choice
  return allowed("ads", visitorIsRegulated(), readConsent());
}

/**
 * Ad unit placed right BELOW a tool's working area (after its result/output), with its height reserved while it
 * loads. Never above or between the tool's inputs and buttons.
 */
export function ToolResultAd() {
  const ok = useAdsAllowed();
  if (!ok) return null;
  return (
    <div className="mt-6 max-w-full overflow-hidden" data-ad-placement="after-tool">
      <AdBanner slot="3878090277" format="auto" reserve={AD_AFTER_TOOL_RESERVE_PX} />
    </div>
  );
}

/**
 * Second unit, lower on the page (after the how-to / questions area). Its height is reserved while it loads too:
 * on a short tool this unit and the footer are already in view, and an ad filling in pushed the footer down.
 */
export function InlineAd() {
  const ok = useAdsAllowed();
  if (!ok) return null;
  return (
    <div className="my-6 max-w-full overflow-hidden" data-ad-placement="lower">
      <AdBanner slot="3878090277" format="auto" reserve={AD_LOWER_RESERVE_PX} />
    </div>
  );
}

/**
 * In-article ad for blog posts. Same gating as InlineAd, but uses AdSense's in-article layout (fluid format),
 * designed to sit inline within long-form text rather than between tool sections.
 */
export function InArticleAd() {
  const ok = useAdsAllowed();
  if (!ok) return null;
  return (
    <div className="my-8 max-w-full overflow-hidden" data-ad-placement="article">
      <AdBanner slot="1649326841" format="fluid" layout="in-article" />
    </div>
  );
}

/**
 * Shown only after the visitor has saved a result (the "freedailypro:download-complete" event), directly below the
 * tool and its first ad unit. It appears after the action is finished, so it never sits between a tool's inputs and
 * its buttons, and it does not exist (no shifted layout) until then.
 */
export function AfterDownloadAd() {
  const ok = useAdsAllowed();
  const [done, setDone] = useState(false);
  useEffect(() => {
    const h = () => setDone(true);
    window.addEventListener("freedailypro:download-complete", h);
    return () => window.removeEventListener("freedailypro:download-complete", h);
  }, []);
  if (!ok || !done) return null;
  return (
    <div className="mt-4 max-w-full overflow-hidden" data-ad-placement="after-download">
      <AdBanner slot="3878090277" format="auto" reserve={AD_AFTER_TOOL_RESERVE_PX} />
    </div>
  );
}

/**
 * Desktop side rail: a 160 px tall unit that sits in the empty margin to the right of the page content, only on
 * screens wide enough (1800 px and up) that it cannot touch the tool. Hidden below that width.
 */
export function SideRailAd() {
  const ok = useAdsAllowed();
  if (!ok) return null;
  return (
    <aside
      className="hidden min-[1800px]:block absolute top-0 right-[-176px] h-full w-[160px] pointer-events-none"
      aria-hidden="true"
      data-ad-placement="side-rail"
    >
      <div className="sticky top-24 pointer-events-auto google-auto-ads-ignore">
        <AdBanner slot="3878090277" format="vertical" reserve={600} />
      </div>
    </aside>
  );
}
